import {
  EmptyPlaceholder,
  ErrorDisplay,
  ListScreensHeader,
  PageContainer,
} from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import { dateValueFormatter } from '@automata/utils/time';
import { Schemas, useListWorkflows } from '@automata/wfb-api';
import Stack from '@mui/material/Stack';
import {
  DataGridPro,
  GridColDef,
  GridSortItem,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { GlobalRoutes } from 'GlobalRoutes';
import { useRootUrl } from 'hooks/useRootUrl';

import { useListWorkflowsSequential } from './useListWorkflowsSequential';

const CustomToolbar = (): JSX.Element => (
  <Stack justifyContent="flex-end" direction="row">
    <GridToolbarQuickFilter />
  </Stack>
);

export function CanvasWorkflowsList(): JSX.Element {
  const sdkInteropListViewFlagEnabled = useFlag(
    'fe.canvas_sdk_interoperability_list_view'
  );
  const workflowsPaginatedApiEnabled = useFlag('fe.workflows_paginated_api');
  const router = useRouter();

  const columns: GridColDef<Schemas.WorkflowInfo>[] = useMemo(
    () => [
      { field: 'name', headerName: 'workflow name', flex: 3 },
      ...(sdkInteropListViewFlagEnabled
        ? [
            {
              field: 'workflow_type',
              headerName: 'created with',
              flex: 3,
            },
          ]
        : []),
      { field: 'creator_username', headerName: 'Creator', flex: 1 },
      {
        field: 'updated_at',
        headerName: 'last modified',
        flex: 1,
        type: 'dateTime',
        getApplyQuickFilterFn: (value: string) => {
          return (params) => {
            return params.formattedValue?.includes(value) ?? false;
          };
        },
        valueFormatter: (value) => dateValueFormatter(value, true),
      },
    ],
    [sdkInteropListViewFlagEnabled]
  );

  const paginatedResult = useListWorkflowsSequential({
    queryParams: { workflow_type: 'canvas' },
    enabled: workflowsPaginatedApiEnabled,
  });

  const standardResult = useListWorkflows(
    {
      queryParams: { workflow_type: 'canvas' },
    },
    {
      enabled: !workflowsPaginatedApiEnabled,
    }
  );

  const { isError, error, data, isLoading, fetchStatus } =
    workflowsPaginatedApiEnabled
      ? paginatedResult
      : {
          isError: standardResult.isError,
          error: standardResult.error,
          data: standardResult.data ?? null,
          isLoading: standardResult.isLoading,
          fetchStatus: standardResult.fetchStatus,
        };

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    {
      field: 'updated_at',
      sort: 'desc',
    },
  ]);
  const { baseRouteWithOrg: editWorkflowBaseUrl } = useRootUrl(
    GlobalRoutes.canvasWorkflowEdit
  );
  const { baseRouteWithOrg: editWorkflowIdBaseUrl } = useRootUrl(
    GlobalRoutes.canvasWorkflowEditID
  );

  const navigateToNewWorkflow = () => {
    router.push(editWorkflowBaseUrl);
  };

  const navigateToExistingWorkflow = (id: string) => {
    router.push(editWorkflowIdBaseUrl.replace(':id', id));
  };

  let content: JSX.Element;
  if (isError) {
    content = (
      <ErrorDisplay
        error={error}
        userMessage="An error occurred while loading the list of workflows. Please try reloading this page."
      />
    );
  } else if (data === null) {
    content = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        <DataGridPro
          sx={{ flexBasis: 0 }}
          rows={[]}
          columns={columns}
          loading={true}
        />
      </div>
    );
  } else if (data.length === 0) {
    content = (
      <EmptyPlaceholder
        title="No workflows exist yet"
        subTitle="Click the blue + button to start"
      />
    );
  } else {
    content = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        <DataGridPro
          sx={{ flexBasis: 0, flexGrow: 1 }}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          rows={data}
          key={data.length}
          sortModel={sortModel}
          loading={
            workflowsPaginatedApiEnabled
              ? false
              : isLoading || fetchStatus === 'fetching'
          }
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          columns={columns}
          onRowClick={({ id }) => navigateToExistingWorkflow(String(id))}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 100 },
            },
          }}
          disableColumnMenu
        />
      </div>
    );
  }

  return (
    <PageContainer>
      <ListScreensHeader
        pageName="Canvas Workflows"
        onConfirmAction={navigateToNewWorkflow}
      />
      {content}
    </PageContainer>
  );
}
